import apiClient from './apiClient';

export interface PushSubscription {
  id: string;
  userId: string;
  organizationId: string;
  endpoint: string;
  expirationTime?: string;
  deviceName?: string;
  isActive: boolean;
  createdAt: string;
  lastNotificationAt?: string;
}

export interface NotificationRecord {
  id: string;
  userId: string;
  title: string;
  body: string;
  icon?: string;
  url?: string;
  type: NotificationType;
  status: NotificationStatus;
  isRead: boolean;
  createdAt: string;
  sentAt?: string;
  readAt?: string;
}

export enum NotificationType {
  AppointmentScheduled = 'AppointmentScheduled',
  AppointmentReminder = 'AppointmentReminder',
  AppointmentCancelled = 'AppointmentCancelled',
  AppointmentConfirmed = 'AppointmentConfirmed',
  PrescriptionCreated = 'PrescriptionCreated',
  LabReportReady = 'LabReportReady',
  RadiologyReportReady = 'RadiologyReportReady',
  VaccinationReminder = 'VaccinationReminder',
  DataSharingRequest = 'DataSharingRequest',
  System = 'System',
  Custom = 'Custom',
}

export enum NotificationStatus {
  Pending = 'Pending',
  Sent = 'Sent',
  Failed = 'Failed',
  Cancelled = 'Cancelled',
}

export interface NotificationListResponse {
  notifications: NotificationRecord[];
  unreadCount: number;
}

export interface VapidPublicKeyResponse {
  publicKey: string;
}

class NotificationService {
  private swRegistration: ServiceWorkerRegistration | null = null;
  private vapidPublicKey: string | null = null;

  /**
   * Initialize the notification service
   */
  async initialize(): Promise<boolean> {
    if (!this.isSupported()) {
      console.warn('Push notifications are not supported in this browser');
      return false;
    }

    try {
      // Register service worker
      this.swRegistration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/',
      });
      console.log('Service Worker registered:', this.swRegistration);

      // Get VAPID public key
      await this.getVapidPublicKey();

      return true;
    } catch (error) {
      console.error('Failed to initialize notification service:', error);
      return false;
    }
  }

  /**
   * Check if push notifications are supported
   */
  isSupported(): boolean {
    return (
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      'Notification' in window
    );
  }

  /**
   * Get current notification permission status
   */
  getPermissionStatus(): NotificationPermission {
    return Notification.permission;
  }

  /**
   * Request notification permission from user
   */
  async requestPermission(): Promise<NotificationPermission> {
    const permission = await Notification.requestPermission();
    return permission;
  }

  /**
   * Check if user is subscribed to push notifications
   */
  async isSubscribed(): Promise<boolean> {
    if (!this.swRegistration) {
      return false;
    }

    const subscription = await this.swRegistration.pushManager.getSubscription();
    return subscription !== null;
  }

  /**
   * Subscribe to push notifications
   */
  async subscribe(deviceName?: string): Promise<PushSubscription | null> {
    if (!this.swRegistration || !this.vapidPublicKey) {
      console.error('Service worker or VAPID key not available');
      return null;
    }

    // Check permission
    if (Notification.permission !== 'granted') {
      const permission = await this.requestPermission();
      if (permission !== 'granted') {
        console.warn('Notification permission denied');
        return null;
      }
    }

    try {
      // Subscribe to push manager
      const subscription = await this.swRegistration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToUint8Array(this.vapidPublicKey) as BufferSource,
      });

      // Get subscription details
      const subscriptionJson = subscription.toJSON();

      // Send subscription to backend
      const response = await apiClient.post<PushSubscription>(
        '/api/notifications/subscribe',
        {
          endpoint: subscriptionJson.endpoint,
          expirationTime: subscriptionJson.expirationTime,
          keys: {
            p256dh: subscriptionJson.keys?.p256dh,
            auth: subscriptionJson.keys?.auth,
          },
          userAgent: navigator.userAgent,
          deviceName: deviceName || this.getDeviceName(),
        }
      );

      console.log('Push subscription created:', response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to subscribe to push notifications:', error);
      return null;
    }
  }

  /**
   * Unsubscribe from push notifications
   */
  async unsubscribe(): Promise<boolean> {
    if (!this.swRegistration) {
      return false;
    }

    try {
      const subscription = await this.swRegistration.pushManager.getSubscription();
      
      if (subscription) {
        // Unsubscribe from backend
        await apiClient.post('/api/notifications/unsubscribe', {
          endpoint: subscription.endpoint,
        });

        // Unsubscribe from push manager
        await subscription.unsubscribe();
        console.log('Push subscription removed');
      }

      return true;
    } catch (error) {
      console.error('Failed to unsubscribe from push notifications:', error);
      return false;
    }
  }

  /**
   * Get user's subscriptions
   */
  async getSubscriptions(): Promise<PushSubscription[]> {
    const response = await apiClient.get<PushSubscription[]>(
      '/api/notifications/subscriptions'
    );
    return response.data;
  }

  /**
   * Get user's notifications
   */
  async getNotifications(
    limit: number = 50,
    unreadOnly: boolean = false
  ): Promise<NotificationListResponse> {
    const response = await apiClient.get<NotificationListResponse>(
      '/api/notifications',
      {
        params: { limit, unreadOnly },
      }
    );
    return response.data;
  }

  /**
   * Get unread notification count
   */
  async getUnreadCount(): Promise<number> {
    const response = await apiClient.get<{ count: number }>(
      '/api/notifications/unread-count'
    );
    return response.data.count;
  }

  /**
   * Mark a notification as read
   */
  async markAsRead(notificationId: string): Promise<boolean> {
    try {
      await apiClient.patch(`/api/notifications/${notificationId}/read`);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Mark all notifications as read
   */
  async markAllAsRead(): Promise<number> {
    const response = await apiClient.patch<{ count: number }>(
      '/notifications/read-all'
    );
    return response.data.count;
  }

  /**
   * Send a test notification
   */
  async sendTestNotification(): Promise<boolean> {
    try {
      await apiClient.post('/notifications/test');
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Get VAPID public key from server
   */
  private async getVapidPublicKey(): Promise<string> {
    if (this.vapidPublicKey) {
      return this.vapidPublicKey;
    }

    // Try environment variable first
    const envKey = import.meta.env.VITE_VAPID_PUBLIC_KEY;
    if (envKey) {
      this.vapidPublicKey = envKey;
      return envKey;
    }

    // Fetch from server
    const response = await apiClient.get<VapidPublicKeyResponse>(
      '/api/notifications/vapid-public-key'
    );
    this.vapidPublicKey = response.data.publicKey;
    return this.vapidPublicKey;
  }

  /**
   * Convert VAPID key from base64 to Uint8Array
   */
  private urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  /**
   * Get device name based on user agent
   */
  private getDeviceName(): string {
    const ua = navigator.userAgent;
    
    if (/iPhone/.test(ua)) return 'iPhone';
    if (/iPad/.test(ua)) return 'iPad';
    if (/Android/.test(ua)) return 'Android Device';
    if (/Windows/.test(ua)) return 'Windows PC';
    if (/Mac/.test(ua)) return 'Mac';
    if (/Linux/.test(ua)) return 'Linux';
    
    return 'Unknown Device';
  }

  /**
   * Show a local notification (without push)
   */
  async showLocalNotification(
    title: string,
    options?: NotificationOptions
  ): Promise<void> {
    if (Notification.permission !== 'granted') {
      console.warn('Notification permission not granted');
      return;
    }

    if (this.swRegistration) {
      await this.swRegistration.showNotification(title, {
        icon: '/icons/icon-192x192.png',
        badge: '/icons/badge-72x72.png',
        ...options,
      });
    }
  }
}

export const notificationService = new NotificationService();
export default notificationService;
